import { createCalculationRowsSelector } from "@/lib/calculation-rows";
import { useEffect, useMemo, useState } from "react";
import { serializeLearningInput, learningFields, type CategoryLearningSettings, type CategorySuggestion } from "@/lib/category-learning";
import type { FinanceCategory } from "@/lib/categories";
import type { Transaction } from "@/lib/finance";

const empty = new Map<string, CategorySuggestion>();

export function useCategorySuggestions(transactions: Transaction[], settings: CategoryLearningSettings, categories: FinanceCategory[], enabled: boolean) {
  const [select] = useState(() => createCalculationRowsSelector(learningFields));
  const learningRows = useMemo(() => select(transactions), [select, transactions]);
  const input = useMemo(() => enabled ? serializeLearningInput(learningRows) : "", [learningRows, enabled]);
  const signature = useMemo(() => `[${input},${JSON.stringify([settings, categories.map((row) => [row.slug, row.kind, row.active, row.system, row.parentSlug])])}]`, [input, settings, categories]);
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState<{ signature: string; suggestions: Map<string, CategorySuggestion>; error: boolean } | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let worker: Worker | undefined;
    let active = true;
    const timer = window.setTimeout(() => {
      try {
        worker = new Worker(new URL("./workers/category-suggestions.worker.ts", import.meta.url), { type: "module" });
        worker.onmessage = (event: MessageEvent<{ entries?: [string, CategorySuggestion][]; error?: boolean }>) => {
          if (active) setResult({ signature, suggestions: new Map(event.data.entries || []), error: Boolean(event.data.error) });
          worker?.terminate();
        };
        worker.onerror = () => {
          if (active) setResult({ signature, suggestions: empty, error: true });
          worker?.terminate();
        };
        worker.postMessage({ input, settings, categories });
      } catch { if (active) setResult({ signature, suggestions: empty, error: true }); }
    }, 150);
    // Cancel superseded work. No polling, requests, or background network traffic.
    return () => { active = false; window.clearTimeout(timer); worker?.terminate(); };
    // Signature excludes finance/note edits and category display changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, enabled, retry]);
  const current = enabled && result?.signature === signature ? result : null;
  return { suggestions: current?.suggestions || empty, pending: enabled && !current,
    error: current?.error || false, retry: () => { setResult(null); setRetry((value) => value + 1); } };
}
